
import { useState, useEffect, useMemo } from 'react';
import AddPhotoButton from './AddPhotoButton';
import styles from './picturesVideo.module.scss';
import CustomFileUploader from '../../../components/CustomFileUploader/CustomFileUploader';
import { uploaderImages } from '../../../constants/uploaderImages';
import { useToast } from '../../../helpers/hooks';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from '@reduxjs/toolkit';
import { postPublicationsImages, deletePublicationImage, setImages } from '../../../actions/salePost';
import Button from '../../../components/Button';
import { PicturesVideoProps, ImageFile } from '../../../interfaces/salePost';
import Tooltip from '@mui/material/Tooltip';
import imageCompression from 'browser-image-compression'


const PicturesVideo = ({
  postPublicationsImages,
  deletePublicationImage,
  fetchingDeleteImage,
  cleanDeleteImage,
  imagesVideos,
  setImages,
  plate,
  isFoundPlate,
  onContinue,
  files,
  onValidityChange
}: PicturesVideoProps) => {
  const [additionalUploaders, setAdditionalUploaders] = useState<number[]>([]);
  const toast = useToast();
  const [disabledButton, setDisabledButton] = useState<boolean>(true);
  const [disableAddMoreButton, setDisableAddMoreButton] = useState<boolean>(true);
  const MAX_IMAGES = 11;
  const requiredIndices = [0, 1, 2, 3, 4];
  const hasRequiredImages = requiredIndices.every(index =>
    (imagesVideos || []).some(img => typeof img.index === 'number' && img.index === index && !img?.toDelete)
  );



  const checkRequiredImages = (images: ImageFile[] = []) => {
    if (!images) {
      setDisabledButton(true);
      setDisableAddMoreButton(true);
      return false;
    }

    const requiredIndices = [0, 1, 2, 3, 4];
    const activeImages = images.filter(img => !img?.toDelete);
    const hasAllRequired = requiredIndices.every(index =>
      activeImages.some(img => typeof img.index === 'number' && img.index === index)
    );

    setDisabledButton(!hasAllRequired);
    setDisableAddMoreButton(activeImages.length >= MAX_IMAGES);

    const totalActiveImages = activeImages.length;

    if (onValidityChange) {
      onValidityChange(hasAllRequired);
    }
    return hasAllRequired;
  };

  useEffect(() => {
    const isValid = checkRequiredImages(imagesVideos);
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [imagesVideos]);

  const handleDeleteImage = (image: ImageFile) => {

    if (image && image.id) {

      if (!image.id.startsWith('temp-')) {

        deletePublicationImage(image.id);
      }

      const currentImages = imagesVideos || [];
      const updatedImages = currentImages.map(img => {
        if (img.id === image.id) {
          return {
            ...img,
            toDelete: true,
            deleted: true,
            previewURL: ""
          };
        }
        return img;
      });


      if (setImages) {

        const activeImages = updatedImages.filter(img => !img.toDelete);
        setImages(updatedImages);
      }
    }
  };

  const anyImageUploading = useMemo(() => {
    return imagesVideos?.some(img => img.upload === "loading");
  }, [imagesVideos]);

  const onChangeFileUploader = async (files: ImageFile[], startIndex: number) => {
    try {

      const validatedFiles = await validateAndLimitFiles(files, startIndex);
      if (!validatedFiles) return;

      const tempFiles = validatedFiles.map(file => ({
        ...file,
        upload: "loading" as "loading" | "success" | "failure",
        id: file.id || `temp-${startIndex}-${Date.now()}`
      }));

      const tempUpdatedImages = manageImageIndexes(tempFiles, startIndex);
      setImages?.(tempUpdatedImages);

      const processedFiles = await processImageFiles(validatedFiles);

      const updatedImages = manageImageIndexes(processedFiles, startIndex);

      await uploadImages(updatedImages);

      updateAdditionalUploaders(updatedImages);
    } catch (error) {
      console.error("Error en onChangeFileUploader:", error);
    }
  };

  const validateAndLimitFiles = async (files: ImageFile[], startIndex: number) => {
    let currentImages = imagesVideos || [];
    const activeCount = currentImages.filter(img => !img.toDelete).length;

    if (activeCount + files.length > 12) {
      const availableSlots = 12 - activeCount;
      if (availableSlots <= 0) {
        toast("Ya alcanzaste el máximo de 12 imágenes permitidas.");
        return null;
      }

      const limitedFiles = files.slice(0, availableSlots);
      toast(`Solo puedes subir ${availableSlots} imagen${availableSlots > 1 ? 'es' : ''} más.`);
      return limitedFiles;
    }

    return files;
  };

  const processImageFiles = async (files: ImageFile[]): Promise<any[]> => {
    return Promise.all(
      files.map(async (file) => {
        try {
          if (!file.file) return file;

          const originalImage = await createImageBitmap(file.file);
          const { canvas, ctx } = createCanvasWithImage(originalImage);

          const recreatedBlob = await canvasToBlob(canvas, file.file.type);

          const compressedFile = await compressImage(recreatedBlob, originalImage);

          const previewURL = URL.createObjectURL(compressedFile);

          return {
            ...file,
            file: compressedFile,
            previewURL,
            name: file.name,
            id: file.id,
          };
        } catch (error) {
          console.error("Error procesando imagen:", error);
          return file;
        }
      })
    );
  };

  const createCanvasWithImage = (image: ImageBitmap) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("No se pudo obtener el contexto del canvas.");

    ctx.drawImage(image, 0, 0);
    return { canvas, ctx };
  };

  const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo crear el Blob desde el canvas"));
      }, mimeType || "image/jpeg", 0.95);
    });
  };

  const compressImage = async (blob: Blob, originalImage: ImageBitmap) => {
    const originalSizeMB = blob.size / (1024 * 1024);

    const options = {
      maxSizeMB: 9,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      preserveExif: false
    };

    if (blob.size > 20 * 1024 * 1024) {
      options.maxWidthOrHeight = 1600;
    }

    try {
      const compressedFile = await imageCompression(blob, options);

      const compressedSizeMB = compressedFile.size / (1024 * 1024);

      return compressedFile;
    } catch (error) {
      const compressedFile = await imageCompression(blob, {
        ...options,
        maxWidthOrHeight: 1200,
        initialQuality: 0.7
      });

      const compressedSizeMB = compressedFile.size / (1024 * 1024);
      return compressedFile;
    }
  };

  const manageImageIndexes = (files: ImageFile[], startIndex: number) => {
    let currentImages = imagesVideos || [];
    let allImages = [...currentImages];
    let newFiles: ImageFile[] = [];

    if (startIndex <= 4) {
      newFiles = files.map(file => ({
        ...file,
        index: startIndex,
        id: file.id || `temp-${startIndex}-${Date.now()}`,
        uploaded: false
      }));
      allImages = allImages.filter(img => img.index !== startIndex);
    } else if (startIndex === 5) {
      const usedIndices = allImages
        .filter(img => img.index >= 5 && !img.toDelete)
        .map(img => img.index);

      let nextIndex = 5;
      while (usedIndices.includes(nextIndex) && nextIndex < 11) {
        nextIndex++;
      }

      newFiles = files.map((file, i) => {
        const index = Math.min(nextIndex + i, 11);
        return {
          ...file,
          index,
          id: file.id || `temp-${index}-${Date.now()}`,
          uploaded: false
        };
      });
    } else {
      newFiles = files.map(file => ({
        ...file,
        index: startIndex,
        id: file.id || `temp-${startIndex}-${Date.now()}`,
        uploaded: false
      }));
      allImages = allImages.filter(img => img.index !== startIndex);
    }

    const updatedImages = [...allImages, ...newFiles];
    setImages?.(updatedImages);
    checkRequiredImages(updatedImages);
    return updatedImages;
  };

  const uploadImages = async (images: ImageFile[]) => {
    const uploadedImages = images.map(img => {
      if (img?.previewURL && img?.file && !img.uploaded && !img.toDelete) {
        const formData = new FormData();
        formData.append("file", img.file, img.name);

        postPublicationsImages(formData, img.id).then((res: any) => {
          if (res?.data?.file?.path) {
            setImages?.((prevImages: any) =>
              prevImages.map((image: any) =>
                image.id === img.id
                  ? {
                    ...image,
                    previewURL: res.data.file.path,
                    upload: "success",
                    path: res.data.file.path,
                    file: undefined,
                  }
                  : image
              )
            );
          }
        });

        return { ...img, upload: "loading" };
      }
      return img;
    });

    setImages?.(uploadedImages);
  };

  const updateAdditionalUploaders = (images: ImageFile[]) => {
    const extraImages = images.filter(img => typeof img.index === 'number' && img.index >= 5 && !img.toDelete);
    if (extraImages.length === 0) return;

    const used = extraImages.map(img => {
      return typeof img.index === 'number' ? img.index - 5 : 0;
    });
    const maxIndex = used.length > 0 ? Math.max(...used) : -1;
    const currentCount = maxIndex + 1;

    const totalUploaded = images.filter(img => !img.toDelete).length;
    const currentAditionals = images.filter(img => typeof img.index === 'number' && img.index >= 5 && !img.toDelete);
    const lastUsedIndex = currentAditionals.length > 0
      ? Math.max(...currentAditionals.map(img => typeof img.index === 'number' ? img.index - 5 : 0))
      : -1;

    const uploaderCount = totalUploaded >= 11
      ? lastUsedIndex + 1
      : lastUsedIndex + 1;

    setAdditionalUploaders(Array.from({ length: uploaderCount }, (_, i) => i));
  };

  useEffect(() => {
    if (imagesVideos) {

      const imagesWithIndices = imagesVideos.map((img: ImageFile, idx: number) => {
        if (img && !img.hasOwnProperty('index')) {
          return { ...img, index: idx };
        }
        return img;
      })
      if (JSON.stringify(imagesWithIndices) !== JSON.stringify(imagesVideos)) {
        setImages && setImages(imagesWithIndices);
      }
      checkRequiredImages(imagesVideos);
    }
  }, [imagesVideos]);

  useEffect(() => {
    if (imagesVideos && imagesVideos.length) {
      const normalized = imagesVideos.map((img, idx) => {
        return {
          ...img,
          upload: "success",
          index: typeof img.index === 'number' ? img.index : idx,
          previewURL: img.previewURL || img.uri || img.url || "",
        };
      });

      setImages && setImages(normalized);
    }
  }, []);
  useEffect(() => {
    if (!imagesVideos || imagesVideos.length === 0) {
      setAdditionalUploaders([]);
      return;
    }

    const activeAdditional = imagesVideos.filter(
      img => img.index >= 5 && !img.toDelete
    );

    if (activeAdditional.length === 0) {
      setAdditionalUploaders([]);
      return;
    }


    const maxIndex = Math.max(...activeAdditional.map(img => img.index));

    const totalActive = imagesVideos.filter(img => !img.toDelete).length;
    const uploaderCount = totalActive >= 12
      ? maxIndex - 5
      : maxIndex - 4;

    setAdditionalUploaders(Array.from({ length: Math.max(uploaderCount, 1) }, (_, i) => i));
  }, [imagesVideos]);


  return (
    <div className={styles.picturesVideoContainer}>
      <div className={styles.topSection}>
        <div className={styles.alertMessage}>
          <p>
            <p className={styles.alertMessageTitle}>Sube 5 fotos para poder publicar. Te recomendamos subir 10 para que tu publicación se muestre mejor y atraiga más interesados.</p><br></br>
            <p className={styles.alertMessageText}>No incluyas bordes, logos, marcas de agua, contacto o textos promocionales. Nuestra IA validará las fotos antes de publicarlo para garantizar la mejor calidad.</p>
          </p>
        </div>
      </div>

      <div className={styles.uploadersGrid}>
        <div className={styles.uploaderItem}>
          <h3>Foto de frente *</h3>
          <div className={styles.coverPhotoWrapper}>
            <CustomFileUploader
              values={imagesVideos?.filter(img => img.index === 0) || []}
              onChange={(files: ImageFile[]) => onChangeFileUploader(files, 0)}
              onDeleteItem={handleDeleteImage}
              className={styles.fileUploader}
              placeholderImage={uploaderImages.front}
              loading={
                imagesVideos?.some(img => img.index === 0 && img.upload === "loading")
              }

              deleting={fetchingDeleteImage === "loading"}

            />
            <div className={styles.coverBadge}>Portada</div>
          </div>
        </div>
        <div className={styles.uploaderItem}>
          <h3>Foto de atrás *</h3>
          <CustomFileUploader
            values={imagesVideos?.filter(img => img.index === 1) || []}
            onChange={(files: ImageFile[]) => onChangeFileUploader(files, 1)}
            onDeleteItem={handleDeleteImage}
            className={styles.fileUploader}
            placeholderImage={uploaderImages.back}
            loading={
              imagesVideos?.some(img => img.index === 1 && img.upload === "loading")
            }

            deleting={fetchingDeleteImage === "loading"}
          />
        </div>
        <div className={styles.uploaderItem}>
          <h3>Foto lateral izquierdo *</h3>
          <CustomFileUploader
            values={imagesVideos?.filter(img => img.index === 2) || []}
            onChange={(files: ImageFile[]) => onChangeFileUploader(files, 2)}
            onDeleteItem={handleDeleteImage}
            className={styles.fileUploader}
            placeholderImage={uploaderImages.leftSide}
            loading={
              imagesVideos?.some(img => img.index === 2 && img.upload === "loading")
            }

            deleting={fetchingDeleteImage === "loading"}
          />
        </div>
        <div className={styles.uploaderItem}>
          <h3>Foto lateral derecho *</h3>
          <CustomFileUploader
            values={imagesVideos?.filter(img => img.index === 3) || []}
            onChange={(files: ImageFile[]) => onChangeFileUploader(files, 3)}
            onDeleteItem={handleDeleteImage}
            className={styles.fileUploader}
            placeholderImage={uploaderImages.rightSide}
            loading={
              imagesVideos?.some(img => img.index === 3 && img.upload === "loading")
            }

            deleting={fetchingDeleteImage === "loading"}
          />
        </div>
        <div className={styles.uploaderItem}>
          <h3>Foto del interior *</h3>
          <CustomFileUploader
            values={imagesVideos?.filter(img => img.index === 4) || []}
            onChange={(files: ImageFile[]) => onChangeFileUploader(files, 4)}
            onDeleteItem={handleDeleteImage}
            className={styles.fileUploader}
            placeholderImage={uploaderImages.interior}
            loading={
              imagesVideos?.some(img => img.index === 4 && img.upload === "loading")
            }

            deleting={fetchingDeleteImage === "loading"}
          />
        </div>


        <Tooltip
          title="Debes subir las fotos obligatorias para poder agregar adicionales"
          disableHoverListener={hasRequiredImages}
          arrow
        >
          <div className={styles.uploaderItem}>
            <h3  >Foto adicional</h3>
            <CustomFileUploader

              values={imagesVideos?.filter(img => img.index === 5) || []}
              onChange={(files: ImageFile[]) => onChangeFileUploader(files, 5)}
              onDeleteItem={handleDeleteImage}
              className={styles.fileUploader}
              placeholderImage={uploaderImages.additional}
              multiple={true}
              disabled={!hasRequiredImages}
              loading={
                imagesVideos?.some(img => img.index === 5 && img.upload === "loading")
              }

              deleting={fetchingDeleteImage === "loading"}
            />
          </div>
        </Tooltip>
        {additionalUploaders.map((index) => (
          <div key={index} className={styles.uploaderItem}>
            <h3>Foto adicional</h3>
            <CustomFileUploader
              values={imagesVideos?.filter(img => img.index === (6 + index)) || []}
              onChange={(files: ImageFile[]) => onChangeFileUploader(files, 6 + index)}
              onDeleteItem={handleDeleteImage}
              className={styles.fileUploader}
              placeholderImage={uploaderImages.additional}
              loading={
                imagesVideos?.some(img => img.index === (6 + index) && img.upload === "loading")
              }

              deleting={fetchingDeleteImage === "loading"}

            />
          </div>
        ))}
      </div>

      <div className={styles.addMoreSection}>

        <Button
          onPress={() => {
            if (onContinue) {
              onContinue();
            }
          }}
          title="Continuar"
          primary
          disabled={disabledButton || anyImageUploading}
          className={styles.continueButton}
        />
      </div>
    </div>
  );
};

export default PicturesVideo;